import { useMemo } from "react";
import { useCalendarData } from "./useCalendarData";
import { useCalendarEnrichment } from "./useCalendarEnrichment";
import { getPartsInTZ, startOfDayInTZ, endOfDayInTZ } from "../lib/timezone";

// =============================================================================
// USE UPCOMING CALLS SNIPPET — Org-Wide for Dashboard Bento Grid
// =============================================================================
// PURPOSE: Shows ALL upcoming calls across all reps in the org (next 14 days).
// Enriched with pre-call compliance dots (green/red).
//
// Reuses useCalendarData + useCalendarEnrichment from the calendar page.
// Unlike MiniCalendar.tsx (rep-scoped), this is org-wide — no rep filter.
// No artificial limit — component handles scrolling.
// =============================================================================

export interface UpcomingCall {
  id: string;
  call_start_time: string;
  lead_name: string | null;
  lead_email: string | null;
  closer_name: string | null;
  has_pre_call: boolean;
  event_type: string;
}

export function useUpcomingCallsSnippet(organizationId: string | undefined, timezone: string) {
  // Fetch 14 days of calls starting now — use org timezone for day boundaries
  const rangeStart = useMemo(() => {
    const now = new Date();
    const parts = getPartsInTZ(now.toISOString(), timezone);
    return startOfDayInTZ(parts.year, parts.month, parts.date, timezone);
  }, [timezone]);

  const rangeEnd = useMemo(() => {
    const now = new Date();
    const parts = getPartsInTZ(now.toISOString(), timezone);
    // today + 13 more days = 14 days total (fence-post: +13, not +14)
    const endDate = new Date(Date.UTC(parts.year, parts.month, parts.date + 13));
    const ep = getPartsInTZ(endDate.toISOString(), timezone);
    return endOfDayInTZ(ep.year, ep.month, ep.date, timezone);
  }, [timezone]);

  const { events, loading } = useCalendarData(organizationId ?? null, rangeStart, rangeEnd);

  const { enrichedEvents } = useCalendarEnrichment(
    organizationId ?? null,
    rangeStart,
    rangeEnd,
    events,
    timezone
  );

  // Filter to future calls only (not cancelled), sort chronologically — no limit, component scrolls
  const upcomingCalls = useMemo((): UpcomingCall[] => {
    const now = new Date();
    return enrichedEvents
      .filter((ev) => {
        if (ev.event_type === "call_cancelled") return false;
        return new Date(ev.call_start_time) > now;
      })
      .sort((a, b) => new Date(a.call_start_time).getTime() - new Date(b.call_start_time).getTime())
      .map((ev) => ({
        id: ev.id,
        call_start_time: ev.call_start_time,
        lead_name: ev.lead_name,
        lead_email: ev.lead_email ?? null,
        closer_name: ev.closer_name,
        has_pre_call: ev.has_pre_call ?? false,
        event_type: ev.event_type,
      }));
  }, [enrichedEvents]);

  const totalUpcoming = useMemo(() => {
    const now = new Date();
    return enrichedEvents.filter(
      (ev) => ev.event_type !== "call_cancelled" && new Date(ev.call_start_time) > now
    ).length;
  }, [enrichedEvents]);

  return { upcomingCalls, totalUpcoming, isLoading: loading };
}
